import type { ButtonProps } from '@mui/material';
import type { GridColDef, GridInitialState } from '@mui/x-data-grid-pro';
import type { WaitlistEntry } from '#services/api/mia.ts';

import * as React from 'react';
import { faUserGroup, faUserPlus } from '@fortawesome/pro-solid-svg-icons';
import { Dialog, FontAwesomeScalableIcon } from '@mend/mui';
import { Alert, Avatar, Box, Button, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { DataGridPro, gridClasses } from '@mui/x-data-grid-pro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { differenceInCalendarDays, format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';

import { activeWaitlist, seededReadQueryKeys } from '#lib/react-query/queries.ts';
import {
  getRemoveWaitlistErrorMessage,
  removeWaitlistEnrollment,
  waitlistRemovalBlockedMessages,
} from '#services/api/mia.ts';
import { formatPhoneNumber } from '#utils/phone.ts';
import { formatReadinessDateTime } from './Readiness.utils.ts';
import { WaitlistEnrollmentForm } from './WaitlistEnrollmentForm.tsx';

const emptyCellPlaceholder = '--';
const lastOfferDateFormat = 'LLL d';
const initialState = {
  sorting: { sortModel: [{ field: 'createdAt', sort: 'asc' }] },
} satisfies GridInitialState;
const columns: GridColDef<WaitlistEntry>[] = [
  {
    field: 'patientId',
    headerName: 'Patient',
    minWidth: 160,
    flex: 1,
    valueGetter: (_value, row) =>
      formatPersonName({
        first: row.patientFirstName,
        last: row.patientLastName,
        fallback: emptyCellPlaceholder,
      }),
  },
  {
    field: 'patientPhone',
    headerName: 'Phone',
    width: 150,
    valueFormatter: (phone: WaitlistEntry['patientPhone']) =>
      phone ? formatPhoneNumber(phone) : emptyCellPlaceholder,
  },
  {
    field: 'appointmentTypeName',
    headerName: 'Visit type',
    minWidth: 130,
    flex: 1,
    valueFormatter: (name: WaitlistEntry['appointmentTypeName']) => name ?? emptyCellPlaceholder,
  },
  {
    field: 'nextAppointment',
    headerName: 'Next scheduled appointment',
    type: 'dateTime',
    align: 'left',
    headerAlign: 'left',
    minWidth: 230,
    flex: 1,
    valueGetter: (_value, row) =>
      row.nextAppointment ? new Date(row.nextAppointment.startDate) : null,
    renderCell: ({ row }) => (
      <Stack direction="column" spacing={0} justifyContent="center" height="100%">
        <Typography variant="body2">
          {formatPersonName({
            first: row.providerFirstName,
            last: row.providerLastName,
            fallback: emptyCellPlaceholder,
          })}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.nextAppointment
            ? formatReadinessDateTime(row.nextAppointment.startDate)
            : 'None scheduled'}
        </Typography>
      </Stack>
    ),
  },
  {
    field: 'createdAt',
    headerName: 'Waiting',
    type: 'dateTime',
    width: 110,
    valueGetter: (_value, row) => new Date(row.createdAt),
    valueFormatter: (createdAt: Date) => {
      const days = differenceInCalendarDays(new Date(), createdAt);
      return `${days} ${days === 1 ? 'day' : 'days'}`;
    },
  },
  {
    field: 'lastOfferedAt',
    headerName: 'Last offer',
    type: 'dateTime',
    minWidth: 120,
    flex: 1,
    valueGetter: (_value, row) => (row.lastOfferedAt ? new Date(row.lastOfferedAt) : null),
    valueFormatter: (lastOfferedAt: Date | null) =>
      lastOfferedAt ? format(lastOfferedAt, lastOfferDateFormat) : 'Not yet offered',
  },
];

/** Organization waitlist; one row per enrollment, never per distinct patient. */
export function WaitlistDialog(props: { orgId: number; onClose: () => void }) {
  const titleId = React.useId();
  const descriptionId = React.useId();
  const confirmationTitleId = React.useId();
  const confirmationDescriptionId = React.useId();
  const query = useQuery(activeWaitlist({ orgId: props.orgId }));
  const [isAddingPatient, setIsAddingPatient] = React.useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = React.useState<WaitlistEntry | null>(null);
  const queryClient = useQueryClient();
  const removal = useMutation({
    mutationFn: removeWaitlistEnrollment,
    onSuccess: async (_response, request) => {
      const { queryKey } = activeWaitlist({ orgId: request.orgId });
      // Prevent an older refetch from restoring the removed enrollment.
      await queryClient.cancelQueries({ queryKey, exact: true });
      queryClient.setQueryData(queryKey, (current) => {
        if (!current) return current;
        const entries = current.entries.filter((entry) => entry.id !== request.waitlistId);
        // Derive the count so repeated successes stay idempotent without blocking dialog dismissal.
        return {
          ...current,
          entries,
          totalCount: entries.length,
        };
      });
      void queryClient.invalidateQueries({ queryKey: seededReadQueryKeys.offerableSlots });
      setSelectedEnrollment(null);
      enqueueSnackbar('Patient enrollment removed from the waitlist', { variant: 'success' });
    },
  });
  const { reset: resetRemoval } = removal;
  const removalColumns = React.useMemo<GridColDef<WaitlistEntry>[]>(
    () => [
      ...columns,
      {
        field: 'actions',
        headerName: 'Actions',
        width: 110,
        sortable: false,
        renderCell: ({ row, tabIndex }) =>
          row.removalBlockedReason !== null ? (
            <Tooltip title={waitlistRemovalBlockedMessages[row.removalBlockedReason]} describeChild>
              <Box component="span" tabIndex={tabIndex}>
                <RemoveButton disabled />
              </Box>
            </Tooltip>
          ) : (
            <RemoveButton
              onClick={() => {
                resetRemoval();
                setSelectedEnrollment(row);
              }}
            />
          ),
      },
    ],
    [resetRemoval]
  );

  return (
    <Dialog.Root
      open
      onClose={props.onClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <Dialog.Header>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar sx={{ bgcolor: 'quaternary.dark', color: 'quaternary.light' }}>
            <FontAwesomeScalableIcon icon={faUserGroup} fontSize="small" />
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Dialog.Title id={titleId}>Waitlist</Dialog.Title>
            <Dialog.Description id={descriptionId} variant="body2">
              Patients waiting for an appointment. Adding a patient does not send a message or
              change existing appointments.
            </Dialog.Description>
          </Box>
        </Stack>
      </Dialog.Header>
      <Dialog.Content>
        {isAddingPatient ? (
          <WaitlistEnrollmentForm orgId={props.orgId} onClose={() => setIsAddingPatient(false)} />
        ) : null}

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          {query.data ? (
            <Typography
              variant="caption"
              component="p"
              color="text.secondary"
              textTransform="uppercase"
            >
              {query.data.totalCount} Patient{' '}
              {query.data.totalCount === 1 ? 'enrollment' : 'enrollments'}
            </Typography>
          ) : (
            <Skeleton variant="rounded" height={20} width={150} />
          )}

          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              startIcon={<FontAwesomeScalableIcon icon={faUserPlus} fontSize="small" />}
              variant="outlined"
              color="neutral"
              disabled={!query.data}
              onClick={() => setIsAddingPatient(true)}
            >
              Add patient
            </Button>
          </Box>
        </Stack>
        {query.isPending ? (
          <Stack role="status" spacing={2}>
            <Typography variant="body2">Loading waitlist...</Typography>
            <Skeleton variant="rounded" height={200} />
          </Stack>
        ) : query.isError ? (
          <Alert severity="error">Waitlist could not load. Please try again later.</Alert>
        ) : (
          <>
            {query.data.entries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No patients are waiting.
              </Typography>
            ) : (
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGridPro
                  aria-label="Active waitlist enrollments"
                  rows={query.data.entries}
                  columns={removalColumns}
                  initialState={initialState}
                  sortingMode="client"
                  rowHeight={72}
                  disableColumnMenu
                  disableColumnFilter
                  disableColumnPinning
                  disableRowSelectionOnClick
                  hideFooter
                  sx={(theme) => ({
                    '--DataGrid-rowBorderColor': theme.palette.divider,
                    borderColor: 'divider',
                    [`& .${gridClasses.withBorderColor}`]: { borderColor: 'divider' },
                    [`& .${gridClasses.columnHeader} .${gridClasses.columnSeparator}`]: {
                      color: 'divider',
                    },
                  })}
                />
              </Box>
            )}
          </>
        )}
      </Dialog.Content>
      <Dialog.Footer>
        <Dialog.DismissButton>Done</Dialog.DismissButton>
      </Dialog.Footer>

      {selectedEnrollment && (
        <Dialog.Root
          open
          fullWidth
          maxWidth="sm"
          onClose={() => setSelectedEnrollment(null)}
          aria-labelledby={confirmationTitleId}
          aria-describedby={confirmationDescriptionId}
        >
          <Dialog.Header>
            <Dialog.Title id={confirmationTitleId}>
              Remove this patient from the waitlist?
            </Dialog.Title>
          </Dialog.Header>
          <Dialog.Content>
            <Stack id={confirmationDescriptionId} direction="column" spacing={1}>
              {removal.isError && (
                <Alert severity="error" onClose={removal.reset}>
                  {getRemoveWaitlistErrorMessage(removal.error)}
                </Alert>
              )}
              <Typography variant="body2">
                This will remove{' '}
                {formatPersonName({
                  first: selectedEnrollment.patientFirstName,
                  last: selectedEnrollment.patientLastName,
                  fallback: 'this patient',
                })}{' '}
                from the waitlist for{' '}
                {selectedEnrollment.appointmentTypeName
                  ? `the visit type ${selectedEnrollment.appointmentTypeName}`
                  : 'this visit type'}{' '}
                with{' '}
                {formatPersonName({
                  first: selectedEnrollment.providerFirstName,
                  last: selectedEnrollment.providerLastName,
                  fallback: 'their provider',
                })}
                .
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No message will be sent to the patient. Adding them again will start a new waiting
                period.
              </Typography>
            </Stack>
          </Dialog.Content>
          <Dialog.Footer>
            <Dialog.DismissButton>Cancel</Dialog.DismissButton>
            <Button
              color="error"
              variant="contained"
              size="large"
              loading={removal.isPending}
              disabled={removal.isPending}
              onClick={() =>
                removal.mutate({ orgId: props.orgId, waitlistId: selectedEnrollment.id })
              }
            >
              Remove
            </Button>
          </Dialog.Footer>
        </Dialog.Root>
      )}
    </Dialog.Root>
  );
}

function RemoveButton(props: ButtonProps) {
  return (
    <Button color="error" variant="outlined" size="small" {...props}>
      Remove
    </Button>
  );
}

function formatPersonName({
  first,
  last,
  fallback,
}: {
  first: string | null;
  last: string | null;
  fallback: string;
}) {
  return [first, last].filter(Boolean).join(' ') || fallback;
}
